import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectField } from '@/common/components/Fields/SelectField/SelectField';
import { Grid } from '@/common/components/Grid/Grid';
import { Modal } from '@/common/components/Modal/Modal';
import { Stack } from '@/common/components/Stack/Stack';
import { useBreakpoints } from '@/common/hooks/useBreakpoints';
import { IMenhirBenefitSlug } from '@/common/integrations/menhir/employee/benefits/benefits.types';
import { useGetEmployeeBudgetChoices } from '@/common/integrations/menhir/employee/budgets/budgets.hooks';
import { parseDate } from '@/common/utils/date';
import { useBenefits } from '@/web/hooks/useBenefits';
import { Option } from '@rn-primitives/select';
import {
  TActivityFilters,
  TActivityProductType,
  getYearFilter,
  getYearsOptions,
  useGetProductOptions,
} from './ActivityFilters.utils';

export type TActivityFiltersProps = {
  filters: TActivityFilters;
  onChange: (filters: TActivityFilters) => void;
  visible?: boolean;
  onToggleVisibility: (visible: boolean) => void;
};

export function ActivityFilters({
  filters,
  onChange,
  visible,
  onToggleVisibility,
}: TActivityFiltersProps) {
  const { t } = useTranslation();
  const breakpoints = useBreakpoints(['sm']);
  const yearOptions = getYearsOptions();
  const productOptions = useGetProductOptions();

  const [year, setYear] = useState(() => {
    const yearOption = parseDate(filters.from).getFullYear();
    return yearOptions.find((option) => option.value === `${yearOption}`);
  });
  const [product, setProduct] = useState<Option>(() => {
    return productOptions.find((option) => option.value === filters.product);
  });

  useEffect(() => {
    if (!product) {
      setProduct(
        productOptions.find((option) => option.value === filters.product)
      );
    }
  }, [product, productOptions, filters.product]);

  const handleOnChange = (newFilters: Partial<TActivityFilters>) => {
    onChange({
      ...filters,
      ...newFilters,
    });
  };

  const filterElements = [
    <SelectField
      key="year"
      id="year"
      value={year}
      label={t('general.labels.filter_by_year')}
      onChange={(newYear) => {
        if (newYear) {
          setYear(newYear);
          handleOnChange({
            from: getYearFilter(Number(newYear.value)).from,
            to: getYearFilter(Number(newYear.value)).to,
          });
        }
      }}
      options={yearOptions}
    />,
    <SelectField
      key="product"
      id="product"
      value={product}
      label={t('general.labels.filter_by_product')}
      onChange={(newProduct) => {
        setProduct(newProduct);
        handleOnChange({
          product: newProduct?.value as TActivityProductType,
        });
      }}
      options={productOptions}
      allowClear
    />,
    product?.value === 'benefits' && (
      <BenefitsSelectField
        key="benefits"
        initialBenefit={filters.benefit}
        onChange={(benefit) => {
          handleOnChange({
            benefit: benefit?.value as IMenhirBenefitSlug,
          });
        }}
      />
    ),
    product?.value === 'budgets' && (
      <BudgetsSelectField
        key="budgets"
        initialBudget={filters.budget}
        onChange={(budget) => {
          handleOnChange({
            budget: budget?.value as string,
          });
        }}
      />
    ),
  ].filter(Boolean);

  if (breakpoints.sm) {
    return (
      <Grid columnBasis="basis-[20rem]" gap="md">
        {filterElements}
      </Grid>
    );
  }

  return (
    visible && (
      <Modal
        title={t('general.labels.filters')}
        onClose={() => onToggleVisibility(false)}
      >
        <Stack gap="md">{filterElements}</Stack>
      </Modal>
    )
  );
}

function BenefitsSelectField({
  initialBenefit,
  onChange,
}: {
  initialBenefit?: IMenhirBenefitSlug;
  onChange: (benefit: Option) => void;
}) {
  const { t } = useTranslation();
  const { benefits } = useBenefits();

  const benefitsOptions = useMemo(() => {
    return (
      benefits?.map((benefit) => ({
        label: t(`general.benefits.${benefit.slug}`),
        value: benefit.slug,
      })) ?? []
    );
  }, [benefits, t]);

  const [benefit, setBenefit] = useState<Option>(() => {
    return benefitsOptions.find((option) => option.value === initialBenefit);
  });

  useEffect(() => {
    if (!benefit) {
      setBenefit(
        benefitsOptions.find((option) => option.value === initialBenefit)
      );
    }
  }, [initialBenefit, benefitsOptions, benefit]);

  return (
    <SelectField
      id="benefit"
      value={benefit}
      label={t('general.labels.filter_by_benefit')}
      onChange={(newBenefit) => {
        setBenefit(newBenefit);
        onChange(newBenefit);
      }}
      options={benefitsOptions}
      allowClear
    />
  );
}

function BudgetsSelectField({
  initialBudget,
  onChange,
}: {
  initialBudget?: string;
  onChange: (budget: Option) => void;
}) {
  const { t } = useTranslation();
  const { employeeBudgetChoices } = useGetEmployeeBudgetChoices();

  const budgetOptions = useMemo(() => {
    return (
      employeeBudgetChoices?.map((budget) => ({
        label: budget.name,
        value: budget.pocketId,
      })) ?? []
    );
  }, [employeeBudgetChoices]);

  const [budget, setBudget] = useState<Option>(() => {
    return budgetOptions.find((option) => option.value === initialBudget);
  });

  useEffect(() => {
    if (!budget) {
      setBudget(budgetOptions.find((option) => option.value === initialBudget));
    }
  }, [initialBudget, budgetOptions, budget]);

  return (
    <SelectField
      id="budget"
      value={budget}
      label={t('general.labels.filter_by_budget')}
      onChange={(newBudget) => {
        setBudget(newBudget);
        onChange(newBudget);
      }}
      options={budgetOptions}
      allowClear
    />
  );
}
